import {
  App, Button, Drawer, Flex, Form, Skeleton,
} from 'antd';
import FormLayout from 'components/FormLayout';
import useIsMobile from 'hooks/useIsMobile';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { captureException } from 'utils/errors';
import { getHttpErrorMessage } from 'utils/http';
import { MenuContext } from '../../constants';
import {
  collectModifierOptionIds,
  isGlobalLinkedItem,
  normalizeModifierOptionRecipesFromApi,
  resolveAttachedModifiers,
  serializeModifierOptionRecipes,
  validateModifierOptionRecipes,
} from 'utils/menu';
import ItemFields from './ItemFields';

function ItemDrawer({
  title, onClose, data, loading: initialLoading, onSubmit, onDelete, onDuplicate,
  open, itemId,
}) {
  const { isGlobal, isCatering, modifiers: allModifiers } = useContext(MenuContext);
  const isMobile = useIsMobile();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { message } = App.useApp();
  const recipeLines = Form.useWatch('recipe_lines', form);
  const modifierOptionRecipes = Form.useWatch('modifier_option_recipes', form);
  const modifierRecipeValidation = useMemo(
    () => validateModifierOptionRecipes(modifierOptionRecipes, recipeLines),
    [modifierOptionRecipes, recipeLines],
  );
  const saveDisabled = isCatering
    && !isGlobalLinkedItem(data?.migration_driver)
    && !modifierRecipeValidation.valid;

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        ...data,
        modifier_option_recipes: normalizeModifierOptionRecipesFromApi(data.modifier_option_recipes),
      });
    }
  }, [data, form]);

  const handleSubmit = async () => {
    if (saveDisabled) {
      message.error(modifierRecipeValidation.errors[0] || 'Fix modifier option recipes before saving.');
      return;
    }
    setLoading(true);
    onSubmit(serialize(form.getFieldsValue(), {
      isGlobal,
      migrationDriver: data?.migration_driver,
      allModifiers,
    }))
      .then(() => {
        message.success(`Item ${itemId ? 'updated' : 'created'} successfully!`);
      })
      .catch((e) => {
        captureException(e);
        message.error(getHttpErrorMessage(e));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Drawer
      title={title}
      placement="right"
      width={isMobile ? '100%' : 575}
      onClose={onClose}
      open={open}
      styles={{ body: { padding: '16px 0 84px' } }}
    >
      {initialLoading && <Skeleton paragraph={{ rows: 10 }} className="px-5" />}
      {!initialLoading && (
        <Form
          form={form}
          layout="vertical"
          onFinishFailed={() => {
            message.error('Please fix errors!');
          }}
          onFinish={handleSubmit}
          initialValues={{
            sizes: [{
              name: '',
              price: null,
              serving_size: null,
            }],
          }}
        >
          <ItemFields disabled={!isGlobal} migrationDriver={data?.migration_driver} />
          <FormLayout.Footer>
            <Flex justify={isGlobal && onDelete ? 'space-between' : 'flex-end'} className="w-full">
              {isGlobal && onDelete && <Button danger disabled={loading} onClick={onDelete}>Delete</Button>}
              <Flex justify="end" gap={8}>
                {onDuplicate ? (
                  <Button disabled={loading} onClick={onDuplicate}>Duplicate</Button>
                ) : (
                  <Button disabled={loading} onClick={onClose}>Close</Button>
                )}
                <Button
                  htmlType="submit"
                  loading={loading}
                  type="primary"
                  disabled={saveDisabled}
                >
                  {itemId ? 'Save' : 'Create'}
                </Button>
              </Flex>
            </Flex>
          </FormLayout.Footer>
        </Form>
      )}
    </Drawer>
  );
}

function serialize(
  {
    images, catering_label, sizes, modifiers, recipe_lines, modifier_option_recipes, ...data
  },
  { isGlobal, migrationDriver, allModifiers = [] } = {},
) {
  const payload = {
    ...data,
    image_ids: images?.map((img) => img.id),
    catering_label: catering_label || 'item',
    sizes: sizes.filter(({ price, name }, index) => price !== null && (index === 0 || name)),
    modifier_ids: modifiers?.map((modifier) => modifier.id),
  };

  if (isGlobal || !isGlobalLinkedItem(migrationDriver)) {
    payload.recipe_lines = (recipe_lines || [])
      .filter((line) => line?.ingredient && Number(line?.quantity) > 0)
      .map((line) => ({
        ingredient: line.ingredient,
        quantity: line.quantity,
      }));
    const serializedRecipes = serializeModifierOptionRecipes(
      modifier_option_recipes,
      recipe_lines,
    );
    const serializedOptionIds = new Set(serializedRecipes.map((entry) => entry.modifier_option));
    const itemModifiers = resolveAttachedModifiers(modifiers, allModifiers);
    collectModifierOptionIds(itemModifiers, allModifiers).forEach((optionId) => {
      if (!serializedOptionIds.has(optionId)) {
        serializedRecipes.push({ modifier_option: optionId, recipe_lines: [] });
      }
    });
    payload.modifier_option_recipes = serializedRecipes;
  }

  return payload;
}

export default ItemDrawer;
